import React, { Fragment } from 'react';
import { Avatar, Box, Divider, Flex, Text } from '@chakra-ui/react';
import Emoji from 'react-emoji-render';
import {
  ChatEventType,
  Participant,
  PossibleEnrichments,
  useIsIdentified,
  Vendor
} from '@outdoorly/react-chat-sdk';
import { Store } from '../../../store';
import { ExpertProfile } from '../../../components/ExpertProfile';
import { MessageActionOptions } from '../../../components/IntroSequence';
import { ChatMessage, MessageOptionActionType } from '../ChatMessage';
import { Recommendation } from '../Recommendation';
import { MotionBox } from '../../../components/Motion';
import { useSendMessage, useSessionInfo, useVendor } from '@outdoorly/react-chat-sdk';
import { EmailCollection } from '../../IntroSequence/EmailCollection';
import { IntroMessageCollection } from '../../../components/IntroSequence/IntroMessageCollection';
import { MessageOptions } from '../MessageOptions';
import { tryToBuildImgixUrl } from '../../../utils/imgix';
import { NetPromoterScore } from '../NetPromoterScore';
import { DashedFlex } from './DashedFlex';
import { LabelVariableSubstitutions } from 'src/utils/labels';
import { EmojiAndLinkifiedText } from '../EmojiAndLinkifiedText';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Timestamp } from '../Timestamp';

dayjs.extend(relativeTime);
export const useEvents = ({
  labelSubstitutions
}: {
  labelSubstitutions: LabelVariableSubstitutions;
}): Record<
  ChatEventType | 'ShowFindingExpert',
  { text?: string | null; additionalInfo?: string | null; emoji?: string | null }
> => {
  const labels = Store.useState((s) => s.labels.substitute(labelSubstitutions), [labelSubstitutions]);
  return {
    Closed: {
      text: labels.conversationClosed,
      emoji: ':wave:',
      additionalInfo: null
    },
    ShowFindingExpert: {
      text: labels.searchingForExpert,
      additionalInfo: labels.waitMessage,
      emoji: ':mag:'
    },
    FindingExpert: {},
    Message: {},
    Escalated: {},
    EscalationFailed: {},
    Joined: {
      text: labels.matchedWithExpert,
      emoji: null,
      additionalInfo: null
    },
    Left: {},
    NPSRequest: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    NameRequest: {},
    NoExpertsAvailable: {
      text: labels.noExpertsAvailable,
      emoji: ':sleeping:',
      additionalInfo: null
    },
    PiiAcknowledged: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    PiiRequest: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    Recommended: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    Reopened: {},
    Started: {},
    TransferFailed: {},
    TransferForced: {},
    TransferRequested: {},
    IdleCancel: {},
    IdleWarningCustomerAbandon: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    IdleWarningExpertAbandon: {
      text: null,
      emoji: null,
      additionalInfo: null
    },
    ChangeConversationDeviceViaURL: {}
  };
};
type MessageContainerProps = {
  children: React.ReactNode;
  isLastMessageInGroup?: boolean;
  vendor?: Vendor;
  expert?: PossibleEnrichments['expert'];
};

const MessageContainer: React.FC<MessageContainerProps> = React.memo((props) => {
  const { children, isLastMessageInGroup, vendor, expert } = props;

  const avatarSource = expert?.expertProfile?.avatar ?? vendor?.logo;
  const avatarUrl = avatarSource
    ? tryToBuildImgixUrl(avatarSource, {
        w: 50,
        h: 50,
        auto: 'format,compress',
        crop: 'faces',
        fit: 'crop'
      })
    : undefined;

  return (
    <Box pos="relative">
      <MotionBox
        pos="absolute"
        initial={{ bottom: '0px', opacity: 0 }}
        animate={{ bottom: '20px', opacity: 1 }}
      >
        {isLastMessageInGroup && (
          <Avatar
            src={avatarUrl}
            w="24px"
            h="24px"
            ignoreFallback
            role="presentation"
            aria-hidden={true}
            name={expert?.expertProfile?.publicName}
          />
        )}
      </MotionBox>
      {children}
    </Box>
  );
});
type MessageEventProps = {
  eventType: PossibleEnrichments['type'] | 'ShowFindingExpert';
  expert?: PossibleEnrichments['expert'];
  recommendation?: PossibleEnrichments['recommendation'];
  isLastMessageInGroup?: boolean;
  dateUpdated?: string;
  options?: Array<MessageActionOptions>;
  id?: string;
  isLastMessage: boolean;
  disableAnimation?: boolean;
  npsRequestId?: PossibleEnrichments['npsRequestId'];
  isPiiCollectionFlow?: boolean;
  npsRequest?: PossibleEnrichments['npsRequest'];
  idleCloseTime?: string;
  email?: { censored?: string; completed?: boolean; skipped?: boolean };
  cancelled?: boolean;
};

export const MessageEvent: React.FC<MessageEventProps> = React.memo((props: MessageEventProps) => {
  const {
    eventType,
    expert,
    recommendation,
    dateUpdated,
    disableAnimation,
    isLastMessageInGroup,
    isLastMessage,
    id,
    email,
    npsRequestId,
    npsRequest,
    isPiiCollectionFlow,
    idleCloseTime,
    cancelled
  } = props;

  const events = useEvents({
    labelSubstitutions: {
      expertName: expert?.name
    }
  });

  const { vendor } = useVendor();

  const event = events[eventType] as { text: string; emoji: string | null; additionalInfo: null | string };

  const messageSender = useSendMessage();

  const labels = Store.useState(
    (s) =>
      s.labels.substitute({
        expertName: expert?.name
      }),
    [expert?.name]
  );

  const { identified } = useIsIdentified();
  if (!eventType || !Object.keys(event).length) return null;

  return (
    // Position relative here is important, as it resolves a scrolling bug in iOS Safari // without it,
    //child elements jump around the screen if the parent is shifted upward // if a later element grows in
    //size.
    <Fragment key={`eventContent-${id}`}>
      <Flex position="relative" justify="center" align="center" flexDir="column" key="eventDescription">
        {event.emoji && <Emoji text={event?.emoji} />}
        {event.text && (
          <DashedFlex role="row">
            <Text
              variant="label-medium"
              color="scheme.contentSecondary"
              textAlign="center"
              maxW="80%"
              aria-live="polite"
              role="gridcell"
              tabIndex={-1}
            >
              {event.text}
            </Text>
          </DashedFlex>
        )}
        {event.additionalInfo && (
          <Text color="scheme.contentTertiary" variant="label-small" letterSpacing="-0.1px" mb="8px">
            {event.additionalInfo}
          </Text>
        )}
      </Flex>
      {eventType === ChatEventType.Joined && (
        <ExpertProfile expertProfile={expert?.expertProfile} key="expertProfile" />
      )}
      {eventType === ChatEventType.Recommended && (
        <MessageContainer
          isLastMessageInGroup={isLastMessageInGroup}
          vendor={vendor}
          expert={expert}
          key="recommendation"
        >
          <Flex flexDir="column">
            <Recommendation
              key={`recommendation-${id}`}
              recommendation={recommendation}
              isLastMessage={isLastMessage}
            />
            <ChatMessage
              body={
                <Text variant="paragraph-medium">
                  <EmojiAndLinkifiedText body={recommendation?.body ?? ''} id={id ?? ''} />
                </Text>
              }
              isExpertOrSystem={true}
              isLastMessageInGroup={isLastMessageInGroup ?? false}
              dateUpdated={dateUpdated ?? ''}
              id={id ?? ''}
              isLastMessage={isLastMessage}
              expertName={expert?.expertProfile?.publicName}
            />
          </Flex>
        </MessageContainer>
      )}
      {eventType === ChatEventType.PiiRequest && (!identified || !isLastMessage) && (
        <Fragment key="piiRequest">
          <MessageContainer vendor={vendor} expert={expert} isLastMessageInGroup={true}>
            <ChatMessage
              body={
                <EmailCollection
                  submitEmail={(email) => {
                    messageSender({ type: 'identify', email });
                  }}
                  emailSubmission={email}
                  emailSkipped={email?.skipped}
                  emailPrompt={labels.whatIsYourEmail}
                  submitLabel={labels.submitIntroMessage}
                />
              }
              isExpertOrSystem={true}
              isLastMessageInGroup={true}
              dateUpdated={dateUpdated ?? ''}
              id={id ?? ''}
              isLastMessage={isLastMessage}
              expertName={expert?.expertProfile?.publicName}
            />
          </MessageContainer>
          {email?.censored && (
            <>
              <MessageContainer vendor={vendor} isLastMessageInGroup={false} expert={expert}>
                <ChatMessage
                  body={
                    <Text variant="paragraph-medium" color="scheme.contentInverted">
                      {email.censored}
                    </Text>
                  }
                  isExpertOrSystem={false}
                  isLastMessageInGroup={false}
                  dateUpdated={dateUpdated ?? ''}
                  id={id ?? ''}
                  isLastMessage={isLastMessage}
                />
              </MessageContainer>
            </>
          )}
          <MessageOptions
            options={[
              { text: labels.skipSecondEmailCollection, actionType: MessageOptionActionType.CancelPII }
            ]}
            isLastMessage={isLastMessageInGroup ?? false}
            pastAnswer={
              email?.skipped ? labels.skipSecondEmailCollection : email?.censored ? email.censored : ''
            }
            disableAnimation={disableAnimation ?? true}
            id={id ?? 'pii-request'}
            dateUpdated={dateUpdated}
          />
          {email?.skipped && (
            <MessageContainer vendor={vendor} isLastMessageInGroup={isLastMessageInGroup} expert={expert}>
              <ChatMessage
                body={
                  <IntroMessageCollection
                    submitMessage={(message) => messageSender(message)}
                    shouldHideSubmission={!isLastMessage}
                    additionalContext
                  />
                }
                isExpertOrSystem={true}
                isLastMessageInGroup={true}
                dateUpdated={dateUpdated ?? ''}
                id={id ?? ''}
                isLastMessage={isLastMessage}
              />
            </MessageContainer>
          )}
        </Fragment>
      )}
      {eventType === ChatEventType.NoExpertsAvailable && (
        <MessageContainer
          expert={expert}
          vendor={vendor}
          isLastMessageInGroup={isLastMessageInGroup}
          key="noExpertsAvailable"
        >
          <Flex flexDir="column">
            <ChatMessage
              body={
                <Text variant="paragraph-medium">
                  {!isPiiCollectionFlow ? (
                    <EmojiAndLinkifiedText body={labels.sorryNoExperts} id={id ?? ''} />
                  ) : (
                    <>
                      <EmojiAndLinkifiedText body={labels.sorryNoExperts} id={id ?? ''} />
                      <br />
                      <br />
                      <EmojiAndLinkifiedText body={labels.youCanLeaveYourEmail} id={id ?? ''} />
                    </>
                  )}
                </Text>
              }
              isExpertOrSystem={true}
              isLastMessageInGroup={false}
              dateUpdated={dateUpdated ?? ''}
              id={id ?? ''}
              isLastMessage={false}
            />
            {!isPiiCollectionFlow && identified && (
              <ChatMessage
                body={
                  <IntroMessageCollection
                    submitMessage={(message) => messageSender(message)}
                    shouldHideSubmission={!isLastMessage}
                    additionalContext
                  />
                }
                isExpertOrSystem={true}
                isLastMessageInGroup={true}
                dateUpdated={dateUpdated ?? ''}
                id={id ?? ''}
                isLastMessage={isLastMessage}
              />
            )}
          </Flex>
        </MessageContainer>
      )}
      {eventType === ChatEventType.PiiAcknowledged && (
        <MessageContainer
          expert={expert}
          vendor={vendor}
          isLastMessageInGroup={isLastMessageInGroup}
          key="PiiAcknowledged"
        >
          <ChatMessage
            body={
              <IntroMessageCollection
                submitMessage={(message) => messageSender(message)}
                shouldHideSubmission={!isLastMessage}
                additionalContext
              />
            }
            isExpertOrSystem={true}
            isLastMessageInGroup={true}
            dateUpdated={dateUpdated ?? ''}
            id={id ?? ''}
            isLastMessage={isLastMessage}
          />
        </MessageContainer>
      )}
      {eventType === ChatEventType.NpsRequest && (
        <Fragment key="npsRequest">
          <MessageContainer expert={expert} vendor={vendor} isLastMessageInGroup={true}>
            <ChatMessage
              body={<Text variant="paragraph-medium">{labels.rateYourConversation}</Text>}
              isExpertOrSystem={true}
              isLastMessageInGroup={true}
              dateUpdated={dateUpdated ?? ''}
              id={id ?? ''}
              isLastMessage={isLastMessage}
            />
          </MessageContainer>
          <NetPromoterScore
            skipped={npsRequest?.skipped ?? false}
            npsRequest={npsRequest}
            npsRequestId={npsRequestId ?? undefined}
          />
        </Fragment>
      )}
      {eventType === ChatEventType.IdleWarningExpertAbandon && !cancelled && (
        <MessageContainer expert={undefined} vendor={vendor} isLastMessageInGroup={isLastMessageInGroup}>
          <ChatMessage
            body={<Text variant="paragraph-medium">{labels.idleWarningExpertAbandon}</Text>}
            isExpertOrSystem={true}
            isLastMessageInGroup={isLastMessageInGroup ?? false}
            dateUpdated={dateUpdated ?? ''}
            id={id ?? ''}
            isLastMessage={isLastMessage}
          />
        </MessageContainer>
      )}
      {eventType === ChatEventType.IdleWarningCustomerAbandon && !cancelled && (
        <Fragment key="idle-warning">
          <MessageContainer expert={undefined} vendor={vendor} isLastMessageInGroup={false}>
            <ChatMessage
              body={<Text variant="paragraph-medium">{labels.idleWarningCustomerAbandon}</Text>}
              isExpertOrSystem={true}
              isLastMessageInGroup={false}
              dateUpdated={dateUpdated ?? ''}
              id={id ?? ''}
              isLastMessage={false}
            />
          </MessageContainer>
          {idleCloseTime && (
            <MessageContainer expert={undefined} vendor={vendor} isLastMessageInGroup={true}>
              <ChatMessage
                body={
                  <Text variant="paragraph-medium">
                    {`⏱ `} <Timestamp time={idleCloseTime} updateDuration={1000} />
                  </Text>
                }
                isExpertOrSystem={true}
                isLastMessageInGroup={true}
                dateUpdated={dateUpdated ?? ''}
                id={id ?? ''}
                isLastMessage={isLastMessage}
              />
            </MessageContainer>
          )}
        </Fragment>
      )}
    </Fragment>
  );
});
